import Bugsnag from '@bugsnag/js';
import { isFeatureEnabled, publicEnvConfig } from 'helpers/env';
import React from 'react';

export function getErrorBoundary() {
	const reactPlugin = Bugsnag.getPlugin('react');

	// Keep the default Next.js dev error experience when Bugsnag is disabled or not initialized.
	if (!reactPlugin?.createErrorBoundary) {
		return function FallbackErrorBoundary({ children }) {
			return children;
		};
	}

	return reactPlugin.createErrorBoundary(React);
}

/**
 * Method to notify errors to Bugsnag
 * @param {string or Error} error what you want to notify
 * @param {object} metadata, you can add extra info here, IMPORTANT: object should have at least 2 levels: { metadata: { cause: 'error explanation' } }
 * @param {function} callback, it will be called with event as parameter, so you can overwrite event values from caller
 */
export function notifyError(error, metadata = {}, callback) {
	const errorMessage = formatError(error);
	const errorToNotify = error instanceof Error ? error : errorMessage;

	// eslint-disable-next-line no-console
	if (!isFeatureEnabled(publicEnvConfig.BUGSNAG_ENABLED)) return console.error({ errorMessage });

	// const user = loggedUSerSelector(getState());

	Bugsnag.notify(errorToNotify, function (event) {
		const groupingHash = getRequestGroupingHash({ error, errorMessage, metadata });
		if (groupingHash) {
			event.groupingHash = groupingHash;
		}
		if (isNetworkError({ metadata })) applyRestClientEventContext(event);

		for (const [key, value] of Object.entries(metadata)) {
			event.addMetadata(key, value);
		}

		callback && callback(event);
	});

	if (publicEnvConfig.DEBUG_MODE) {
		// eslint-disable-next-line no-console
		console.error(error);
	}
}

/**
 * Method to notify request/network errors to Bugsnag with consistent metadata.
 * @param {string or Error} error what you want to notify
 * @param {object} requestContext request context metadata
 * @param {function} callback beforeSend callback
 */
export function notifyNetworkError(error, requestContext = {}, callback) {
	const { source = 'restClient', ...restRequestContext } = requestContext;
	const metadata = {
		errorContext: {
			source,
			isNetworkError: true,
			...restRequestContext,
		},
	};

	return notifyError(error, metadata, callback);
}

function applyRestClientEventContext(event) {
	if (event.groupingHash) event.context = event.groupingHash;
}

function formatError(error) {
	if (typeof error === 'string') {
		return error;
	} else if (error && Object.prototype.hasOwnProperty.call(error, 'message')) {
		return error.message;
	} else if (error && error.status) {
		// request fail format.
		const formatted = {
			message: 'request failing',
			status: error.status,
			url: error?.error?._currentUrl,
			data: error?.error?.data,
		};

		// Surface the original error string or object for debugging
		if (typeof error.error === 'string') {
			formatted.rawError = error.error;
		} else if (error.error && typeof error.error === 'object') {
			// If error.error is an object, try to include its message or stringified version
			if (error.error.message) {
				formatted.rawError = error.error.message;
			} else {
				formatted.rawError = safeStringify(error.error);
			}
		}

		return safeStringify(formatted);
	}

	return safeStringify(error);
}

function safeStringify(error) {
	try {
		return JSON.stringify(error);
	} catch (stringificationError) {
		return stringificationError;
	}
}

const isNetworkError = ({ metadata }) => {
	const errorContext = metadata?.errorContext || {};
	return errorContext?.isNetworkError;
};

function getRequestGroupingHash({ metadata }) {
	const requestContext = metadata?.errorContext || {};
	// Keep custom grouping scoped to RestClient request failures only.
	if (!isNetworkError({ metadata })) return null;

	const normalizedRoute = normalizeRouteForGrouping(requestContext?.endpoint);
	return `${String(requestContext?.method).toUpperCase()}:${requestContext?.status || 'status'}:${normalizedRoute}`;
}

function normalizeRouteForGrouping(urlValue) {
	if (!urlValue || typeof urlValue !== 'string') return 'unknown-route';

	const trimmedUrl = urlValue.trim();
	let pathname = trimmedUrl;

	try {
		const parsedUrl = new URL(trimmedUrl, 'https://openlane.local');
		pathname = parsedUrl.pathname || trimmedUrl;
	} catch {
		pathname = trimmedUrl.split('?')[0];
	}

	return pathname
		.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, ':uuid')
		.replace(/\b[A-Z0-9]{17}\b/g, ':vin')
		.replace(/\b\d+\b/g, ':id')
		.toLowerCase();
}

export const getSetUserCallback = (user) => (event) => event.setUser(user.id, user.email);
